import React, { useState, useEffect, useMemo, useRef } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsList from '../core/HeadersAndFooters/FooterButtonsList';
import UnifiedLocationSelector from '../locality/UnifiedLocationSelector';
// Old LocationSelector and LocalityMapSelector replaced by UnifiedLocationSelector
import AreaColorPicker from './AreaColorPicker';
import AreaIdentityFields from '../shared/AreaIdentityFields';
import LanguageSelector from '../core/LanguageSelector';
import ConfirmationModal from '../core/ConfirmationModal';
import StandardButton from '../core/StandardButton';
import { deriveAreaIdentityFromName, generateUniqueAreaCode, isAreaCodeCollision, isBaseParishRefusal } from '../../utils/homeAreaSelfHealer';
import { getBoundaryNamesInOrder, getAreaParishes } from '../../data/directoryService';
import ParishSelect from '../shared/ParishSelect';
import { AREA_COLORS, getUsedColors, isColorAvailable } from '../../config/areaColors';
import { themes } from '../../themes/index.js';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import { getCountryFlag } from '../../utils/countryHelpers';

// Default longitudes per country — used to centre the map for home areas
const COUNTRY_DEFAULT_LNG = { PT: -8.0, GB: -3.5 };

// 2026-08-29 area-code collision fix, extended to all home-creation paths
// (docs/briefs/2026-08-29-area-code-collision-all-home-paths.md): generateUniqueAreaCode and
// isAreaCodeCollision now live in src/utils/homeAreaSelfHealer.js — one shared implementation for
// every home-area write path (this file, ChangeHomeAreaStep.jsx, RegisterLocationStep.jsx,
// createHomeAreaFromBoundary), not five copies. Originally built local to this file
// (docs/briefs/2026-08-29-mylocations-area-code-collision-fix.md); behaviour is unchanged —
// same random 6-char uppercase+digit format (matches the DB's own CHECK constraints, confirmed
// live via pg_constraint), same retry-capped uniqueness check, same 409/23505 detection.

// 2026-08-29 UI layout follow-on (docs/briefs/2026-08-29-mylocations-ui-layout-followon.md, item 1):
// selection-depth shadow for the left-column/mobile-grid slot buttons. A local, documented value —
// not tokens.card.shadow.raised (Advisor-caught: that token is scoped to card-CONTENT geometry,
// LAYOUT.md's Card Primitive section, paired specifically with the "wide" 10px-radius variant; tying
// an unrelated concept — button selection state — to it risks a future card-specific redesign
// silently dragging this along). Same numeric value as this file's own existing card shadows,
// chosen for visual consistency, not because it IS that token.
const SELECTED_SLOT_SHADOW = '0 2px 8px rgba(0, 0, 0, 0.1)';

const MyLocations = ({ userId, onBack, currentContext, autoHomeHighlight = false, onAutoHomeHighlightDismiss, setHomeSeedBoundaryId = null }) => {
  // Always use Locality theme for this page (not Groups)
  const theme = themes.locality;
  const { isMobile } = useMediaQuery();
  const { t, locale } = useTranslation('user');
  const { t: tLocation } = useTranslation('location');
  const [userAreas, setUserAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('PT'); // Track selected country for flag display
  // userLocalities removed - using boundary_ids only
  const [loading, setLoading] = useState(true);
  const [authUserId, setAuthUserId] = useState(null);
  // 2026-08-26 redesign + create-path build: retires the "+ Create Area" modal
  // (docs/briefs/2026-08-26-mylocations-redesign-and-create-path-build.md). isCreatingArea
  // marks "selected an empty slot, no user_areas row yet" — the row is born at the first
  // boundary resolve (see handleAreaBoundaryChange), not at this click.
  const [isCreatingArea, setIsCreatingArea] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(null); // null or area.id
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null, onCancel: null, cancelLabel: null });
  // Bumped after a refused boundary save, so the map selector remounts and shows what is saved
  // (it reads initialBoundaries only when it mounts).
  const [selectorResetCount, setSelectorResetCount] = useState(0);
  // "Your own parish" (own-parish addenda 2 and 3): the selected area's parishes, named, in their
  // saved order. Every saved (named) area with more than one parish offers a choice, home or not;
  // the same test decides whether the refusal message may point at the picker.
  const offersOwnParish = (area) =>
    !!(area?.id && area?.display_name && (area?.boundary_ids || []).length > 1);
  const [ownParishOptions, setOwnParishOptions] = useState([]);
  const ownParishAreaKey = offersOwnParish(selectedArea)
    ? `${selectedArea.id}|${(selectedArea.boundary_ids || []).join(',')}|${selectedArea.primary_boundary_id ?? ''}`
    : null;
  useEffect(() => {
    if (!ownParishAreaKey) {
      setOwnParishOptions([]);
      return undefined;
    }
    let cancelled = false;
    getAreaParishes(selectedArea).then(({ options, error }) => {
      if (cancelled) return;
      if (error) console.warn('Your own parish: parish names could not be read:', error);
      setOwnParishOptions(options || []);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownParishAreaKey]);

  // The refusal when the base parish would leave its area; where the area offers "Your own
  // parish" it also says where to change it (an unsaved area being created has no picker).
  const baseParishMustStayMessage = async (area) => {
    const message = t('locations_base_parish_must_stay', { parish: await baseParishName(area?.primary_boundary_id) });
    return offersOwnParish(area) ? `${message} ${t('locations_own_parish_change_hint')}` : message;
  };

  // "Make this my own parish": the new base parish is one of the area's parishes (so the database
  // guard is satisfied), and it moves to the front of the list, where areas keep their base
  // parish. Header, weather, directory and new posts follow via ournet:refresh-locations.
  const handleSetOwnParish = async (areaId, parishId) => {
    const area = userAreas.find(a => a.id === areaId);
    const ids = (area?.boundary_ids || []).map(Number);
    if (!area || !ids.includes(Number(parishId)) || Number(area.primary_boundary_id) === Number(parishId)) return;
    const token = activeAreaTokenRef.current;
    try {
      const authToken = await supabaseFetch.getAuthToken();
      const { error } = await supabaseFetch.update('user_areas', {
        primary_boundary_id: Number(parishId),
        boundary_ids: [Number(parishId), ...ids.filter(id => id !== Number(parishId))],
        updated_at: new Date().toISOString()
      }, { eq: { id: areaId }, authToken });
      if (error) throw new Error(error.message);
      await loadUserAreas(areaId, token);
      window.dispatchEvent(new CustomEvent('ournet:refresh-locations'));
    } catch (err) {
      console.error('Error changing own parish:', err);
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_own_parish_failed'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  // The base parish's name, for the refusal message; empty if it cannot be read.
  const baseParishName = async (boundaryId) => {
    if (!boundaryId) return '';
    const { data } = await getBoundaryNamesInOrder([boundaryId]);
    return data?.[0]?.name || '';
  };

  // 2026-08-28 write-path fix (docs/briefs/2026-08-28-mylocations-write-path-fix.md): same
  // ref-mirror / stale-async-guard idiom as App.js's activeContextRef/currentViewRef
  // (App.js:269-291) — every async area-load captures this token before its own await and
  // re-checks it on resolve; a newer user action (select a different area, start a new
  // create) bumps the token and turns any still-in-flight load's eventual setSelectedArea
  // into a no-op instead of a stale snap-back onto whatever the user has since left.
  const activeAreaTokenRef = useRef(0);
  // Mirrors selectedArea for the logo-click listener below, whose effect intentionally
  // doesn't depend on selectedArea (would re-bind the listener on every area switch) — same
  // ref-mirror idiom as the token ref above.
  const selectedAreaRef = useRef(null);
  useEffect(() => { selectedAreaRef.current = selectedArea; }, [selectedArea]);
  // The boundary id the crosshair actually seeded as the create's target — as opposed to
  // whatever ends up at ids[0] after grows/deselects reorder the array. Captured in
  // handleTargetSeeded below; written as the new row's primary_boundary_id at insert
  // (Advisor STRONGLY ADVISE: array-position is not the target once the user can deselect it).
  const seededTargetIdRef = useRef(null);
  // Guards the create-insert branch of handleAreaBoundaryChange against a second entry (a
  // rapid grow-click landing before the first insert's await resolves) firing a duplicate
  // INSERT for what the user experiences as one area (Advisor STRONGLY ADVISE).
  const insertInFlightRef = useRef(false);
  // 2026-08-29 create/heal-under-latency fix (docs/briefs/2026-08-29-create-heal-under-latency.md,
  // Fault A, Advisor STRONGLY ADVISE): the create attempt's token, snapshotted at
  // handleStartCreateArea — BEFORE the crosshair's own two pre-insert round trips
  // (resolve_point_to_boundary, then get_boundaries_by_ids inside UnifiedLocationSelector's
  // handleSetTargetLocation) even begin. Reading activeAreaTokenRef.current fresh inside
  // handleAreaBoundaryChange itself (the previous approach) captures too late — a Back click
  // landing during either of those two RPCs bumps activeAreaTokenRef.current BEFORE
  // handleAreaBoundaryChange is even invoked, so a same-tick read there always sees the
  // already-bumped value and compares it against itself, never detecting the abandonment. This
  // ref is the one stable value captured at the true start of a create attempt.
  const createTokenRef = useRef(null);

  // Re-define-home hand-off (2026-09-01 home-picker-redefine-flow-fix-build): the "set-home
  // session" is deliberately split into two pieces of state, tied to two different lifetimes —
  // NOT one screen-level boolean (Advisor STRONGLY ADVISE on the pre-build consultation: a bare
  // flag would still read as "active" if the user abandoned the seeded create and switched to an
  // unrelated existing area, making that area's own later Save-and-Exit unconditionally — and
  // wrongly — promote it to home).
  // activeSeed: live only while the SEEDED create is still in progress (drives seedBoundaryId +
  // adjacency auto-fill below); cleared the moment that create attempt ends, one way or another.
  const [activeSeed, setActiveSeed] = useState(() => setHomeSeedBoundaryId ? { boundaryId: setHomeSeedBoundaryId } : null);
  // setHomeSessionAreaId: the specific area id (not a boolean) allowed to have Save-and-Exit
  // promote it unconditionally — set once, only when the seeded create's own INSERT succeeds;
  // checked by exact id match in handleSaveAndExit, so switching to any other area never reads
  // as "in a set-home session" for that other area.
  const [setHomeSessionAreaId, setSetHomeSessionAreaId] = useState(null);
  // Captured at the top of a create attempt (handleStartCreateArea/the mount-seed effect), read
  // by handleAreaBoundaryChange's create branch when the INSERT resolves — same
  // capture-before-awaits idiom as createTokenRef just above, so a stale read can't cross a
  // meanwhile-abandoned attempt.
  const pendingSetHomeCreateRef = useRef(false);

  // Auto-home highlight state (set by self-healer when a home area was auto-created)
  const [isHighlightActive, setIsHighlightActive] = useState(autoHomeHighlight);

  const dismissHighlight = () => {
    setIsHighlightActive(false);
    if (onAutoHomeHighlightDismiss) onAutoHomeHighlightDismiss();
  };

  // Selectable admin levels per country — loaded once at mount from get_available_levels.
  // selectableLevels: Set of strings like "PT:7", "PT:8", "GB:6", "GB:7".
  // allLevelData: flat array of level objects with country_code attached.
  const [selectableLevels, setSelectableLevels] = useState(new Set());
  const [allLevelData, setAllLevelData] = useState([]);

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      // 2026-08-28 write-path fix (Rule 4): leaving MyLocations entirely via the logo must
      // not leave an in-progress, never-named create behind. Reads selectedAreaRef, not
      // selectedArea, since this effect deliberately doesn't depend on it.
      // 2026-08-28 round two (Advisor STRONGLY ADVISE): bumping the token here, same as
      // handleSelectArea/handleStartCreateArea already do, closes the race where the logo is
      // clicked before a just-started create's insert has resolved — selectedAreaRef is still
      // null at that instant, so the cleanup call above can't reach the not-yet-existing row.
      // The bump routes the insert's own pending loadUserAreas call into its stale-token
      // self-sweep branch (see loadUserAreas below) instead of the normal branch, so it cleans
      // up the nameless row itself once the insert resolves — reusing round one's mechanism
      // rather than adding a second one.
      activeAreaTokenRef.current += 1;
      cleanupNamelessArea(selectedAreaRef.current);
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBack]);

  // 2026-08-28 write-path fix (Rule 4): same cleanup for the header Back button.
  // 2026-08-28 round two (Advisor STRONGLY ADVISE): same token bump as the logo listener above,
  // for the same reason — Back can be clicked before an in-flight create's insert resolves.
  const handleBack = () => {
    activeAreaTokenRef.current += 1;
    cleanupNamelessArea(selectedArea);
    if (onBack) onBack();
  };

  // Helper function to capitalize first letter of each word
  const capitalizeWords = (str) => {
    if (!str) return str;
    return str.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Get authenticated user ID from localStorage (supabase.auth.getUser() can hang)
  useEffect(() => {
    try {
      const storageKey = Object.keys(localStorage).find(
        k => k.startsWith('sb-') && k.endsWith('-auth-token')
      );
      if (storageKey) {
        const storedData = JSON.parse(localStorage.getItem(storageKey) || '{}');
        if (storedData?.user?.id) {
          setAuthUserId(storedData.user.id);
        }
      }
    } catch (err) {
      console.error('Error reading auth from localStorage:', err);
    }
  }, []);

  // Load selectable levels for all countries (PT + GB) once at mount.
  // Used by grandfather checks and i18n level label interpolation.
  useEffect(() => {
    const loadSelectableLevels = async () => {
      try {
        const levels = new Set();
        const levelRows = [];
        for (const cc of ['PT', 'GB']) {
          const { data } = await supabaseFetch.rpc('get_available_levels', { p_country_code: cc });
          if (data) {
            data.forEach(l => {
              levelRows.push({ ...l, country_code: cc });
              if (l.is_user_selectable) levels.add(`${cc}:${l.admin_level}`);
            });
          }
        }
        setSelectableLevels(levels);
        setAllLevelData(levelRows);
      } catch (err) {
        console.error('Error loading selectable levels:', err);
      }
    };
    loadSelectableLevels();
  }, []);

  useEffect(() => {
    // Addendum 1 (2026-09-01 home-picker-redefine-flow-fix-build): expectedToken, captured here —
    // same staleness idiom every other loadUserAreas call in this file already uses (handleRefresh
    // etc.), just never applied to this one, the initial mount load. Without it, this call's own
    // default-selection ("no preserveSelectedAreaId -> select home, or first row", :551-554)
    // unconditionally overwrote whatever a same-mount seeded create (the effect below) had just
    // set, once its async round trip resolved — landing the set-home hand-off back on the OLD
    // home every time, regardless of timing, not as a race that sometimes lost. Passing the token
    // captured NOW (before the seed effect below has a chance to bump it) means: no seed this
    // mount -> token unchanged -> not stale -> today's default-select behaviour, unaffected; a
    // seed this mount -> startCreateArea's own bump makes this call's own selection a no-op,
    // exactly like every other stale reload in this file already stands down.
    loadUserAreas(null, activeAreaTokenRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleRefresh = () => {
      // Preserve currently selected area when refreshing
      loadUserAreas(selectedArea?.id, activeAreaTokenRef.current);
    };

    window.addEventListener('ournet:refresh-locations', handleRefresh);
    return () => {
      window.removeEventListener('ournet:refresh-locations', handleRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (showColorPicker === null) return;

    const handleClickOutside = () => {
      setShowColorPicker(null);
    };

    // Add listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showColorPicker]);

  // Country of the area being edited — captured when ULS detects the country
  // from loaded boundaries. Used for compliance checks (not the picker's current
  // selection, which can drift if the user switches countries mid-edit).
  const [areaCountry, setAreaCountry] = useState('PT');

  // Check if a boundary's admin_level is selectable for the area's country.
  // Falls back to PT L8 (freguesia only) if selectableLevels hasn't loaded yet.
  // Not currently called — the count-based "grandfather" mechanism that used it was
  // retired (Part-1a); kept for the Part-1b home-selection UI to reuse.
  const isLevelSelectable = (adminLevel) => {
    if (selectableLevels.size === 0) {
      // Fallback while loading — PT default (freguesia only)
      return adminLevel === 8;
    }
    return selectableLevels.has(`${areaCountry}:${adminLevel}`);
  };

  // Build a human-readable label for selectable levels in the current country.
  // e.g. "Municipality or Parish" (EN) / "Concelho ou Freguesia" (pt-PT).
  // Uses Intl.ListFormat for correct locale-aware "or" conjunction.
  const getSelectableLevelsLabel = () => {
    const countryLevels = allLevelData
      .filter(l => l.country_code === selectedCountry && l.is_user_selectable)
      .sort((a, b) => a.admin_level - b.admin_level)
      .map(l => locale === 'pt-PT' ? (l.level_label_pt || l.level_label) : l.level_label);
    if (countryLevels.length === 0) return '';
    try {
      return new Intl.ListFormat(locale, { type: 'disjunction' }).format(countryLevels);
    } catch {
      return countryLevels.join(' / ');
    }
  };

  // Handler for when UnifiedLocationSelector loads initial boundaries.
  // 2026-09-02 GB saved-area country sync fix (Advisor-caught): reads the country straight off
  // the loaded boundary data, not off this component's own selectedCountry state. The two
  // callbacks (onCountryChange then onInitialLoad) can land in the same tick, and this
  // component's selectedCountry closure only updates on the next render — reading selectedCountry
  // here risked snapshotting the pre-sync value. The loaded boundary itself is the source of
  // truth for its own country, so read it directly instead of depending on callback order.
  const handleInitialBoundariesLoad = (data) => {
    const loadedCountry = data?.[0]?.country_code;
    setAreaCountry(loadedCountry || selectedCountry);
  };

  // Rich-crosshair identity box (2026-08-26 save-and-exit/parity build, Part 1) — mirrors
  // ChangeHomeAreaStep.jsx's own identity state/handlers exactly (same "override wins" shape),
  // but reset per-selectedArea (MyLocations swaps between areas via the sidebar; ChangeHomeAreaStep
  // only ever has one home to load, once, on mount). Any EXISTING non-blank value is treated as
  // custom/frozen, freezing avoids silently overwriting a name the user already chose, same
  // reasoning as that build's own STRONGLY ADVISE.
  // Known residual (2026-08-26 redesign+create-path build): since the retired create modal was
  // replaced with insert-at-first-boundary (area_code is NOT NULL, so a derived placeholder is
  // written even when the user typed nothing), a freshly-created row's area_code always reads as
  // non-blank here and gets marked areaCodeCustom=true on select — even when it's still just the
  // auto-derived placeholder. A user who re-seeds a different target before Save-and-exit won't
  // see the code re-derive for the new target; they can still edit it manually, and Save-and-exit's
  // own validation still gates on it being well-formed. Accepted for the pilot, not engineered
  // around — flagged so it isn't mistaken for an oversight.
  const [identity, setIdentity] = useState({
    displayName: '', areaCode: '', colorHex: null,
    displayNameCustom: false, areaCodeCustom: false, colorCustom: false
  });

  useEffect(() => {
    if (!selectedArea) return;
    // 2026-08-29 name-default fix (docs/briefs/2026-08-29-mylocations-name-default-suggested-name.md):
    // displayName falls back to suggested_name (the freguesia name seeded at create, persisted
    // separately from display_name so round two's orphan sweep — which tests only display_name —
    // stays untouched). displayNameCustom deliberately does NOT fold suggested_name into its check
    // (Advisor STRONGLY ADVISE): unlike area_code, which is now random and location-independent
    // (2026-08-29 area-code collision fix), display_name is still location-derived — marking
    // custom=true off a mere suggestion would freeze handleTargetSeeded's re-derivation the moment
    // a fresh row loads, so re-seeding the crosshair to a DIFFERENT freguesia before ever saving
    // would silently leave the name field stuck on the first freguesia, and a save would then
    // persist a name that no longer matches the area's actual location. Only a real display_name
    // (an actual save, or an explicit edit via handleIdentityNameChange) freezes it.
    setIdentity({
      displayName: selectedArea.display_name ?? selectedArea.suggested_name ?? '',
      areaCode: selectedArea.area_code || '',
      colorHex: selectedArea.color_hex ?? null,
      displayNameCustom: !!selectedArea.display_name,
      areaCodeCustom: !!selectedArea.area_code,
      colorCustom: selectedArea.color_hex != null
    });
  }, [selectedArea?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2026-08-29 polish (docs/briefs/2026-08-29-mylocations-polish-name-colour-selected-home-top.md,
  // item 2): colour is now auto-assigned to the next unused palette entry rather than always
  // "Sky Blue" — same "is this colour already taken by one of my areas" helpers AreaColorPicker
  // already uses. Shared by handleTargetSeeded's transient derivation and
  // handleAreaBoundaryChange's create-branch write, so both compute the same value.
  const pickAutoColor = () =>
    AREA_COLORS.find(c => isColorAvailable(c.hex, getUsedColors(userAreas)))?.hex || AREA_COLORS[0].hex;

  // 2026-08-29 area-code collision fix, extended: generateUniqueAreaCode is the shared
  // implementation (src/utils/homeAreaSelfHealer.js) now — passing this user's already-loaded
  // userAreas as existingCodes keeps this a local check, no DB round-trip, same as the original
  // local-only version. The shared function is async (its no-existingCodes branch queries the DB
  // for callers without a pre-loaded list), so handleTargetSeeded below awaits it before its
  // areaCodeCustom decision, rather than inside the setIdentity updater.
  const handleTargetSeeded = async (target) => {
    seededTargetIdRef.current = target?.id ?? null;
    // 2026-08-29 area-code collision fix: display_name is still derived from the freguesia name
    // (deriveAreaIdentityFromName, unchanged) — only area_code stops being location-derived, per
    // Michael's ruling. handleAreaBoundaryChange's create branch (below) generates its own code
    // independently for the actual write; this transient value briefly shown here settles to the
    // true persisted code once the insert+reload resolves (sub-second) — same class of gap as the
    // colour auto-assign's two independent computations, judged low-visibility enough not to
    // engineer around (Save-and-exit is disabled until selectedArea exists, so the settled value is
    // always correct by the time it could be acted on).
    const { displayName } = deriveAreaIdentityFromName(target.name);
    const areaCode = identity.areaCodeCustom
      ? identity.areaCode
      : await generateUniqueAreaCode(authUserId || userId, { existingCodes: userAreas.map(a => a.area_code) });
    setIdentity(prev => ({
      ...prev,
      displayName: prev.displayNameCustom ? prev.displayName : displayName,
      areaCode: prev.areaCodeCustom ? prev.areaCode : areaCode,
      colorHex: prev.colorCustom ? prev.colorHex : (prev.colorHex ?? pickAutoColor())
    }));
  };

  const handleIdentityNameChange = (value) => setIdentity(prev => ({ ...prev, displayName: value, displayNameCustom: true }));
  const handleIdentityCodeChange = (value) => setIdentity(prev => ({ ...prev, areaCode: value.toUpperCase(), areaCodeCustom: true }));
  const handleIdentityColorChange = (hex) => setIdentity(prev => ({ ...prev, colorHex: hex, colorCustom: true }));

  // Save-and-exit (Part 2) for MyLocations: persists any pending identity edits (boundary_ids are
  // already persisted per-click by onBoundaryChange below — nothing new needed there) and does NOT
  // navigate (destination is "back to MyLocations" — already there). Gated behind the same
  // three-part code validation the retired create-modal's handleCreateArea used to apply
  // (non-blank, length===6, uppercase-alnum) — Advisor-caught pre-build: AreaIdentityFields' own
  // validation is weaker (length only), and this is a NOT NULL, DB-unconstrained column two other
  // render sites in this file already read back assuming it's well-formed.
  // Addendum 2 (2026-09-01 home-picker-redefine-flow-fix-build): navigateAway=false lets the
  // seeded set-home area's "Make Home" click reuse this exact validate-persist-promote sequence
  // without the "return to the previous page" behaviour that's specific to the Save-and-Exit
  // button — every other Make Home click (an already-saved area, via handleSetAreaAsHome below)
  // never navigates away either, so this keeps the two entry points looking/behaving alike.
  const handleSaveAndExit = async ({ navigateAway = true } = {}) => {
    if (!selectedArea) return;
    // 2026-08-28 UX companion fix (docs/briefs/2026-08-28-mylocations-ux-companion.md, Fault 1):
    // was one combined check reporting every failure as a code-format error, even when the
    // actual empty field was the name. Each field now validates — and reports — independently,
    // in the same top-to-bottom order AreaIdentityFields renders them (name, then code).
    if (!identity.displayName || !identity.displayName.trim()) {
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_name_required'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }
    const code = identity.areaCode;
    if (!code || code.length !== 6 || !/^[A-Z0-9]+$/.test(code)) {
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_code_format'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }
    // 2026-08-29 polish (item 2): colour is now compulsory — same top-to-bottom order as
    // AreaIdentityFields renders (name, code, colour). Auto-assignment (pickAutoColor, above)
    // means this is a safety net, not the primary gate — should almost never fire in normal use.
    if (!identity.colorHex) {
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_color_required'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }
    const targetAreaId = selectedArea.id;
    const token = activeAreaTokenRef.current;
    try {
      const authToken = await supabaseFetch.getAuthToken();
      // 2026-08-29 area-code collision fix: this call's {error} return was previously never
      // inspected at all — supabaseFetch.update() never throws on a non-ok response, it resolves
      // to {data: null, error: {...}}, so ANY failure here (23505 or otherwise) was silently
      // swallowed: the code fell straight through to setSelectedArea/loadUserAreas/onBack as if
      // the save had succeeded, navigating away with the edit silently lost. Now checked — an
      // edited area_code that collides with one of the user's own gets a human message instead of
      // either the raw Postgres dialog or, previously, no feedback at all.
      const { error: updateError } = await supabaseFetch.update('user_areas', {
        display_name: identity.displayName,
        area_code: identity.areaCode,
        color_hex: identity.colorHex,
        updated_at: new Date().toISOString()
      }, {
        eq: { id: targetAreaId },
        authToken
      });
      if (updateError) {
        if (isAreaCodeCollision(updateError)) {
          setModal({
            visible: true,
            title: t('common:error'),
            message: t('locations_error_code_collision'),
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
          return;
        }
        throw new Error(updateError.message);
      }

      // 2026-08-29 is_home database enforcement (docs/briefs/2026-08-29-is-home-database-enforcement.md):
      // the moment this area genuinely becomes named/committed is the moment it's allowed to
      // become home — reproduces "first area is home" without ever granting is_home to a
      // nameless row (see the create branch's own comment, and
      // docs/escalations/2026-08-29-mylocations-create-not-routed-through-atomic-rpc.md).
      // p_only_if_no_home:true makes the "does the user have zero homes" check and the
      // promotion itself one locked transaction inside the RPC — NOT a client-side check
      // followed by a separate call, which an Advisor pass (round 2) caught as reintroducing
      // this same brief's target race in a new, unprotected spot (a concurrent legitimate write
      // between a JS-side check and the RPC call could get silently demoted). If the user
      // already has a different home (including one that appeared concurrently since this
      // screen loaded), the RPC itself detects that atomically and no-ops — this area simply
      // isn't home, which is correct. Failure here doesn't fail the save itself (already
      // succeeded above) — logged only; the self-healer remains the backstop if this user is
      // still home-less on their next load.
      // 2026-09-01 home-picker-redefine-flow-fix-build: a set-home session's own area promotes
      // UNCONDITIONALLY on save — p_only_if_no_home would no-op here, since the whole point of
      // this flow is a user who already HAS a home. Checked by exact id match against
      // setHomeSessionAreaId (not a screen-level flag — see that state's own declaration comment
      // for why), so this branch can only ever fire for the one area this specific seeded create
      // produced, never a different area the user has since switched to. Same
      // atomic-blanket-demote-then-set RPC either way — no new race, per the brief's own
      // instruction not to reintroduce a two-call create-then-promote sequence.
      if (!selectedArea.is_home) {
        const isSetHomeTarget = setHomeSessionAreaId === targetAreaId;
        const { error: homeError } = await supabaseFetch.rpc(
          'promote_area_to_home',
          isSetHomeTarget ? { p_area_id: targetAreaId } : { p_area_id: targetAreaId, p_only_if_no_home: true }
        );
        if (homeError) {
          console.error('Error claiming home on save (non-fatal — save itself succeeded):', homeError.message);
        } else if (isSetHomeTarget) {
          setSetHomeSessionAreaId(null); // consumed — one-shot, matching activeSeed's own lifetime
        }
      }

      // 2026-08-28 write-path fix: a functional-updater merge onto `prev` unconditionally
      // would apply this save's identity fields to whatever area the user has since switched
      // to — guard on both the token and the id so a switch mid-save can't cross-contaminate
      // the wrong card (Rule 1).
      if (activeAreaTokenRef.current === token) {
        setSelectedArea(prev => (prev?.id === targetAreaId
          ? { ...prev, display_name: identity.displayName, area_code: identity.areaCode, color_hex: identity.colorHex }
          : prev));
      }
      await loadUserAreas(targetAreaId, token);
      // 2026-08-28 UX companion fix (Fault 2): the button is labelled and intended as a
      // terminal action ("save, then return to the previous page") but never actually
      // navigated — a plain missing call, per the brief's own read of the evidence.
      if (navigateAway && onBack) onBack();
    } catch (err) {
      console.error('Error saving area identity:', err);
      setModal({
        visible: true,
        title: t('common:error'),
        message: err.message,
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  // 2026-08-28 write-path fix: cleans up an in-progress, never-named create row (Rule 4 —
  // "a nameless row means never committed"). Never touches a row that has a display_name.
  const cleanupNamelessArea = async (area) => {
    if (!area || area.display_name) return;
    try {
      const authToken = await supabaseFetch.getAuthToken();
      await supabaseFetch.delete('user_areas', { eq: { id: area.id }, authToken });
      setUserAreas(prev => prev.filter(a => a.id !== area.id));
    } catch (err) {
      console.error('Error cleaning up nameless area:', err);
    }
  };

  // expectedToken (2026-08-28 write-path fix): the caller's activeAreaTokenRef snapshot at
  // the time it kicked off this load. If a newer user action (select/create) has since
  // bumped the token, this resolution is stale — the fresh area list is still applied (safe),
  // but selecting into it is skipped so it can't stomp over where the user has since moved.
  // If the row this stale call would have selected is itself a nameless in-progress create,
  // it's swept here rather than left as an orphan nobody else is tracking (Advisor STRONGLY
  // ADVISE — this is the exact PONT01 orphan class, reachable via ordinary network latency).
  const loadUserAreas = async (preserveSelectedAreaId = null, expectedToken = null) => {
    try {
      setLoading(true);
      const userIdToUse = authUserId || userId;
      console.log('📍 loadUserAreas: Loading for user:', userIdToUse);

      // Load all user's areas using supabaseFetch (supabase client hangs)
      const { data, error } = await supabaseFetch.query('user_areas', {
        select: '*',
        eq: { user_id: userIdToUse },
        order: 'created_at.asc'
      });

      console.log('📍 loadUserAreas: Result - count:', data?.length, 'error:', error);
      if (error) throw error;

      setUserAreas(data || []);

      // Try to preserve currently selected area, otherwise select home or first
      let areaToSelect = null;
      if (preserveSelectedAreaId) {
        areaToSelect = (data || []).find(a => a.id === preserveSelectedAreaId);
      }
      if (!areaToSelect) {
        const homeArea = (data || []).find(a => a.is_home);
        areaToSelect = homeArea || data?.[0];
      }

      if (expectedToken != null && expectedToken !== activeAreaTokenRef.current) {
        if (preserveSelectedAreaId && areaToSelect?.id === preserveSelectedAreaId && !areaToSelect.display_name) {
          await cleanupNamelessArea(areaToSelect);
        }
        return;
      }

      if (areaToSelect) {
        setSelectedArea(areaToSelect);
        // loadAreaLocalities removed - using boundary_ids only
      }
    } catch (err) {
      console.error('Error loading areas:', err);
    } finally {
      setLoading(false);
    }
  };

  // loadAreaLocalities removed - areas now defined by boundary_ids only

  // 2026-08-26 redesign + create-path build: entry point for both empty-slot clicks (desktop
  // and mobile). Replaces the retired "+ Create Area" modal — deselects any current area and
  // resets the identity box to blank, then drops the user straight into the crosshair in
  // "create mode" (isCreatingArea, no selectedArea yet). Nothing is written here; the row is
  // born at the first boundary resolve (handleAreaBoundaryChange below), per the brief's
  // insert-at-first-boundary ruling.
  // 2026-09-01 home-picker-redefine-flow-fix-build: extracted to startCreateArea(isSetHomeAttempt)
  // so the mount-time seeded entry (below) and an ordinary empty-slot click share one path, while
  // only the former captures pendingSetHomeCreateRef — an ordinary click must never inherit a
  // leftover set-home seed (see activeSeed's own clear below).
  const startCreateArea = (isSetHomeAttempt = false) => {
    // 2026-08-28 write-path fix (Rule 4): leaving an in-progress, never-named area behind to
    // start another create must not leave it stranded.
    cleanupNamelessArea(selectedArea);
    activeAreaTokenRef.current += 1;
    // 2026-08-29 create/heal-under-latency fix (Fault A): snapshot the bumped token as THIS
    // create attempt's own token, before any of the crosshair's own round trips begin.
    createTokenRef.current = activeAreaTokenRef.current;
    pendingSetHomeCreateRef.current = isSetHomeAttempt;
    if (!isSetHomeAttempt) setActiveSeed(null); // an ordinary create must not reuse a stale seed
    seededTargetIdRef.current = null;
    setSelectedArea(null);
    setIdentity({
      displayName: '', areaCode: '', colorHex: null,
      displayNameCustom: false, areaCodeCustom: false, colorCustom: false
    });
    setIsCreatingArea(true);
  };
  const handleStartCreateArea = () => startCreateArea(false);

  // 2026-09-01 home-picker-redefine-flow-fix-build: kicks off the seeded create once, on mount,
  // when MyProfile's existing-home picker handed a core boundary id off to this page. Runs before
  // the crosshair instance itself ever mounts (isCreatingArea gates its render further down), so
  // seedBoundaryId is already correct on that instance's own first render — no remount-keying
  // needed (Advisor-flagged risk on the pre-build consultation: UnifiedLocationSelector's own
  // enrichment effect only seeds once, on ITS mount).
  useEffect(() => {
    if (setHomeSeedBoundaryId) startCreateArea(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared onBoundaryChange for both desktop and mobile UnifiedLocationSelector instances.
  // Branches on whether a user_areas row exists yet:
  //  - isCreatingArea (no selectedArea): this IS the first boundary resolve — absorbs the
  //    retired modal's gap list (probe report §6): row creation, the at-cap pre-check, the
  //    not-logged-in guard, the same is_home-on-first-area decision handleCreateArea made
  //    (Advisor STRONGLY ADVISE — omitting it can leave a user with no home area at all), and
  //    explicit auto-select of the new row via loadUserAreas(newArea.id).
  //  - otherwise: today's unchanged UPDATE path.
  const handleAreaBoundaryChange = async (ids, boundaries) => {
    // Validate: all boundaries must be from the same country
    if (boundaries && boundaries.length > 0) {
      const countries = new Set(boundaries.map(b => b.country_code).filter(Boolean));
      if (countries.size > 1) {
        setModal({ visible: true, title: t('common:error'), message: tLocation('location.saved_areas.error_mixed_countries', { countries: Array.from(countries).join(', ') }), onOk: () => setModal({ visible: false, message: '', onOk: null }) });
        return; // Block the save
      }
    }

    if (isCreatingArea && !selectedArea) {
      // 2026-08-28 write-path fix (Advisor STRONGLY ADVISE): a second entry into this branch
      // while the first insert's await is still in flight (a rapid grow-click on the map
      // before isCreatingArea/selectedArea have updated) would otherwise fire a duplicate
      // INSERT for what the user experiences as one area.
      if (insertInFlightRef.current) return;

      const userIdToUse = authUserId || userId;
      if (!userIdToUse) {
        setModal({
          visible: true,
          title: t('common:error'),
          message: t('locations_error_not_logged_in'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
        return;
      }
      if (userAreas.length >= 10) {
        setModal({
          visible: true,
          title: t('common:error'),
          message: t('locations_error_max_areas'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
        setIsCreatingArea(false);
        return;
      }

      // area_code is NOT NULL (confirmed live against staging). display_name is nullable — left
      // blank unless customised; Save-and-exit's existing validation forces a real value before
      // either can be exited on.
      // 2026-08-29 name-default fix (docs/briefs/2026-08-29-mylocations-name-default-suggested-name.md,
      // rebuild of item 1 after the earlier escalation — docs/escalations/2026-08-29-mylocations-
      // persisted-name-vs-orphan-sweep.md, now resolved): display_name STAYS null-until-saved here —
      // unchanged, still the sweep's untouched signal. The seeded freguesia name instead goes to the
      // new suggested_name column (below), which cleanupNamelessArea does not test — a genuinely
      // unsaved row is still swept on leave, even though it displayed a name.
      // 2026-08-29 area-code collision fix (docs/briefs/2026-08-29-mylocations-area-code-collision-fix.md,
      // extended 2026-08-29-area-code-collision-all-home-paths.md): area_code no longer derives
      // from the resolved boundary's name (deriveAreaIdentityFromName, still called below for
      // suggestedName only) — that derivation is deterministic on location, so re-picking a
      // location used in an earlier session always re-derived the SAME code, colliding with
      // (user_id, area_code)'s unique constraint (23505, the dialog Michael hit). Random +
      // collision-checked instead, via the shared generator (existingCodes avoids a DB round-trip
      // — userAreas is already loaded).
      const areaCode = identity.areaCodeCustom
        ? identity.areaCode
        : await generateUniqueAreaCode(userIdToUse, { existingCodes: userAreas.map(a => a.area_code) });
      const displayName = identity.displayNameCustom ? identity.displayName : null;
      // 2026-08-29 name-default fix: written unconditionally (not gated on displayNameCustom) — a
      // fixed record of what was seeded at create, per the brief's literal wording. When the user
      // already typed a custom name before ever clicking Set, display_name above already carries
      // that value directly and the display fallback (display_name ?? suggested_name) ignores this.
      const suggestedName = deriveAreaIdentityFromName(boundaries?.[0]?.name).displayName;
      // 2026-08-29 polish (item 2): colour is now compulsory, auto-assigned to the next unused
      // palette entry — computed here rather than read from identity.colorHex, because
      // handleTargetSeeded (which derives it) fires AFTER this branch starts, same tick (seedTarget
      // calls onBoundaryChange before onTargetSeeded) — identity.colorHex's closure is always
      // whatever handleStartCreateArea reset it to (null) at this point, one click behind. Respects
      // a colour the user picked manually before ever clicking Set (identity.colorCustom).
      const colorHex = identity.colorCustom && identity.colorHex ? identity.colorHex : pickAutoColor();
      // 2026-08-28 write-path fix (Advisor STRONGLY ADVISE): the seeded target, not ids[0] —
      // a grow/deselect can reorder ids so the array's first element is no longer the target
      // the crosshair actually resolved. Falls back to ids[0] only if no target was ever
      // seeded (defence-in-depth; the create flow always seeds one before this branch runs).
      const primaryBoundaryId = seededTargetIdRef.current ?? ids?.[0] ?? null;
      // 2026-08-29 create/heal-under-latency fix (Fault A, Advisor STRONGLY ADVISE): the
      // attempt's token, snapshotted at handleStartCreateArea — see createTokenRef's own comment.
      // Reading activeAreaTokenRef.current fresh here would be too late (this line itself is
      // already downstream of the crosshair's own two pre-insert round trips).
      const token = createTokenRef.current;
      // 2026-09-01 home-picker-redefine-flow-fix-build: same capture-before-awaits idiom as
      // `token` immediately above — read once, here, before this attempt's own async gaps, so a
      // meanwhile-started ordinary create (which resets this ref to false) can't be mistaken for
      // a set-home one, or vice versa.
      const isSetHomeCreate = pendingSetHomeCreateRef.current;

      insertInFlightRef.current = true;
      try {
        const authToken = await supabaseFetch.getAuthToken();
        // 2026-08-29 is_home database enforcement (docs/briefs/2026-08-29-is-home-database-enforcement.md):
        // every create writes is_home:false, unconditionally, including a user's first area — no
        // client-side "am I first" check at all any more (closing the prior session's narrowing
        // fix, which was still a check-then-write race, not a real fix). is_home is granted
        // later, at Save-and-exit, the moment this row genuinely becomes named — see
        // handleSaveAndExit's own promote_area_to_home call below, and
        // docs/escalations/2026-08-29-mylocations-create-not-routed-through-atomic-rpc.md for why
        // this branch doesn't route through create_home_area_if_none_exists (the escalation this
        // fix carries): that RPC requires a committed display_name, which would defeat the
        // suggested_name/orphan-sweep design this file already ships. display_name stays
        // null-until-saved here, completely unchanged, for every create with no exception.
        const { data, error } = await supabaseFetch.insert('user_areas', {
          user_id: userIdToUse,
          area_code: areaCode,
          display_name: displayName,
          suggested_name: suggestedName,
          color_hex: colorHex,
          boundary_ids: ids,
          primary_boundary_id: primaryBoundaryId,
          is_auto_detected: false,
          is_home: false
        }, { authToken });
        if (error) throw error;

        // 2026-08-28 round two write-path fix (docs/briefs/2026-08-28-mylocations-write-path-round-two.md,
        // Fault A): supabaseFetch.insert() already unwraps its response to the single inserted
        // row object (src/lib/supabase.js — `data: Array.isArray(result) ? result[0] : result`),
        // not an array — `data?.[0]` here was indexing an object with key "0", always undefined.
        // newArea?.id was therefore always undefined, so the loadUserAreas call below always fell
        // through to its default-selection logic (home area, or first row) instead of selecting
        // the just-created row — the render bound to home while the write (unaffected by this,
        // it doesn't reference newArea) targeted the correct row. Root cause of both Fault A (the
        // editor/map showing home after Set) and Fault B (the nameless-orphan sweep always
        // checking home's selectedArea, which has a display_name, instead of the actual orphan).
        const newArea = data;

        // 2026-08-29 create/heal-under-latency fix (Fault A): check staleness IMMEDIATELY, using
        // the INSERT's own returned row directly — not a subsequent re-query's .find(), which
        // depends on that reload's SELECT already reflecting the just-committed INSERT. With
        // createTokenRef's earlier snapshot (above), this now correctly detects a Back/logo/
        // select/another-create landing at ANY point since this attempt began — including during
        // the crosshair's own two pre-insert round trips, not just during this INSERT.
        if (activeAreaTokenRef.current !== token) {
          await cleanupNamelessArea(newArea);
          return;
        }

        // 2026-09-01 home-picker-redefine-flow-fix-build: the seed's job ends here — it seeded
        // the crosshair and gated the adjacency auto-fill, both now done. From this point the
        // "may Save-and-Exit promote unconditionally" question is owned by setHomeSessionAreaId,
        // scoped to this exact row's id, not by activeSeed/isSetHomeCreate any more.
        if (isSetHomeCreate && newArea?.id) {
          setSetHomeSessionAreaId(newArea.id);
        }
        setActiveSeed(null);

        setIsCreatingArea(false);
        dismissHighlight();
        await loadUserAreas(newArea?.id, token);
        window.dispatchEvent(new CustomEvent('ournet:locations-changed'));
      } catch (err) {
        console.error('Error creating area:', err);
        // 2026-08-29 area-code collision fix: a residual collision the generator's own retry
        // didn't pre-empt (generateUniqueAreaCode already checks against userAreas, so this is the
        // 23505-on-genuinely-stale-state backstop, not the primary defence) — human message, never
        // the raw Postgres dialog Michael hit.
        setModal({
          visible: true,
          title: t('common:error'),
          message: isAreaCodeCollision(err)
            ? t('locations_error_code_collision')
            : isBaseParishRefusal(err)
              ? await baseParishMustStayMessage({ primary_boundary_id: primaryBoundaryId, is_home: false })
              : err.message,
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } finally {
        insertInFlightRef.current = false;
      }
      return;
    }

    if (!selectedArea) return;

    // The area's own parish cannot be deselected (own-parish addendum 2; the database refuses it
    // too, from migration 20260929). Only a real deselect is refused — the base parish was in the
    // saved list and is not in the new one — so an area saved before 20260929 without its base
    // parish in the list can still be edited.
    const savedIds = (selectedArea.boundary_ids || []).map(Number);
    const baseId = selectedArea.primary_boundary_id != null ? Number(selectedArea.primary_boundary_id) : null;
    if (baseId != null && savedIds.includes(baseId) && !(ids || []).map(Number).includes(baseId)) {
      setModal({
        visible: true,
        title: t('common:error'),
        message: await baseParishMustStayMessage(selectedArea),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      setSelectorResetCount(n => n + 1);
      return;
    }

    // Update the user_area with new boundary_ids
    try {
      const authToken = await supabaseFetch.getAuthToken();
      const { error: saveError } = await supabaseFetch.update('user_areas', {
        boundary_ids: ids,
        is_auto_detected: false,
        updated_at: new Date().toISOString()
      }, {
        eq: { id: selectedArea.id },
        authToken
      });
      // supabaseFetch.update() resolves with the error rather than throwing, so a refusal used to be
      // lost here. The database refuses a list without the area's base parish (own-parish
      // addendum 1): say so, and put the map back to what is saved. Nothing changed, so no
      // locations-changed event.
      if (saveError) {
        console.error('Error saving boundary IDs:', saveError);
        setModal({
          visible: true,
          title: t('common:error'),
          message: isBaseParishRefusal(saveError)
            ? await baseParishMustStayMessage(selectedArea)
            : t('common:generic_error'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
        setSelectorResetCount(n => n + 1);
        return;
      }

      // Update local state
      setSelectedArea(prev => ({ ...prev, boundary_ids: ids }));
      dismissHighlight();

      // Emit event to refresh feeds
      window.dispatchEvent(new CustomEvent('ournet:locations-changed'));
    } catch (err) {
      console.error('Error saving boundary IDs:', err);
    }
  };

  // 2026-08-28 write-path fix: selecting an area to view/edit must never change is_home
  // (Rule 2) — the previous "automatically set this area as home when selected" behaviour
  // is what stole SOMA01's home flag (Advisor-confirmed root cause: the create→Set desync
  // forces the user to click the new area's own slot to correctly bind it, and that click
  // ran through this handler). Only the explicit "Set Home" button below now promotes home.
  const handleSelectArea = (area) => {
    // Rule 4: leaving an in-progress, never-named area behind to look at a different one
    // must not leave it stranded.
    if (selectedArea?.id !== area.id) {
      cleanupNamelessArea(selectedArea);
    }
    activeAreaTokenRef.current += 1;
    setIsCreatingArea(false);
    // 2026-09-01 home-picker-redefine-flow-fix-build: an abandoned seeded create's own row is
    // already correctly swept above/below by the existing nameless-orphan machinery; this just
    // retires the seed itself so a later "+ add area" click can't inherit it (belt-and-braces —
    // startCreateArea's own isSetHomeAttempt=false path already clears it too).
    setActiveSeed(null);
    // Post-diff Advisor pass: cleanupNamelessArea's own delete is never error-checked (matching
    // this project's documented supabaseFetch pattern — it resolves {error}, never throws), so an
    // abandoned seeded row could in principle survive server-side after a silent delete failure.
    // Clearing here too (not just relying on the row's later non-existence) means even a
    // resurfaced, re-selected, and re-saved copy of that row can no longer read as "in a set-home
    // session" purely because this id happens to match — it would need a fresh seeded create to
    // earn that again.
    if (setHomeSessionAreaId && selectedArea?.id === setHomeSessionAreaId) {
      setSetHomeSessionAreaId(null);
    }
    setSelectedArea(area);
    // loadAreaLocalities removed - using boundary_ids only
  };

  // 2026-08-26 create-new-home build: routed through the atomic promote_area_to_home RPC —
  // replaces the previous SELECT-all-areas + N separate is_home=false UPDATEs (one per row) +
  // one final is_home=true UPDATE, a live non-atomic gap the probe confirmed
  // (docs/briefs/2026-08-26-change-home-create-new-model-probe-report.md, items 3/5): a failure
  // partway through the clearing loop could leave the user with zero or two home areas, visible
  // to the ~12 independent is_home readers elsewhere in the app. One RPC call, one transaction.
  const handleSetAreaAsHome = async (areaId) => {
    const userIdToUse = authUserId || userId;

    if (!userIdToUse) {
      // 2026-08-28 UX companion fix (Advisor STRONGLY ADVISE, TRANSLATION.md BREAKING rule —
      // hardcoded English, unreachable by audit-translations.js): same underlying condition
      // (no resolved user id) as the create path's own guard, so reusing its key rather than
      // inventing a near-duplicate.
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_not_logged_in'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    const token = activeAreaTokenRef.current;
    try {
      const { error: rpcError } = await supabaseFetch.rpc('promote_area_to_home', {
        p_area_id: areaId
      });
      if (rpcError) throw new Error(rpcError.message);

      dismissHighlight();
      await loadUserAreas(areaId, token);
      window.dispatchEvent(new CustomEvent('ournet:refresh-locations'));
    } catch (err) {
      console.error('Error setting home area:', err);
      // 2026-08-28 UX companion fix (Advisor STRONGLY ADVISE): was a raw template-literal
      // string — hardcoded English, invisible to audit-translations.js — matching this file's
      // own established "translated prefix + raw runtime error" shape (see
      // locations_error_color_update_prefix below).
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_set_home_failed') + ' ' + err.message,
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  const handleDeleteArea = async (areaId, isHome) => {
    if (isHome) {
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_cannot_delete_home'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    setModal({
      visible: true,
      message: t('locations_confirm_delete_area'),
      cancelLabel: t('common:cancel'),
      onOk: async () => {
        setModal({ visible: false, message: '', onOk: null });
        const token = activeAreaTokenRef.current;
        try {
          // 2026-08-28 write-path fix: explicit authToken pass, matching the other write
          // paths in this file (Advisor ADVISE — this delete was the one write call in the
          // file omitting it).
          const authToken = await supabaseFetch.getAuthToken();
          await supabaseFetch.delete('user_areas', {
            eq: { id: areaId },
            authToken
          });

          await loadUserAreas(null, token);
          window.dispatchEvent(new CustomEvent('ournet:refresh-locations'));
        } catch (err) {
          console.error('Error deleting area:', err);
          setModal({
            visible: true,
            title: t('common:error'),
            message: err.message,
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
        }
      },
      onCancel: () => setModal({ visible: false, message: '', onOk: null })
    });
  };

  const handleColorChange = async (areaId, colorHex) => {
    const token = activeAreaTokenRef.current;
    try {
      console.log('Updating area color:', { areaId, colorHex });

      // 2026-08-28 write-path fix: explicit authToken pass, matching the other write paths
      // in this file (Advisor ADVISE — this update was the other write call omitting it).
      const authToken = await supabaseFetch.getAuthToken();
      const { error } = await supabaseFetch.update('user_areas',
        { color_hex: colorHex },
        { eq: { id: areaId }, authToken }
      );

      if (error) throw new Error(error.message);

      // Close picker
      setShowColorPicker(null);

      // Reload areas to reflect new color
      await loadUserAreas(selectedArea?.id, token);

      // Notify other components (LocalitySelector in HeaderButtonsFeed)
      window.dispatchEvent(new CustomEvent('ournet:refresh-locations'));

      console.log('Area color updated successfully');
    } catch (err) {
      console.error('Error updating area color:', err);
      setModal({
        visible: true,
        title: t('common:error'),
        message: t('locations_error_color_update_prefix') + err.message,
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    }
  };

  // handleToggleLocality removed - using boundary_ids only

  // handleRemoveLocality and handleMoveLocality removed - using boundary_ids only

  // savedLocalityIds removed - using boundary_ids only

  // 2026-08-29 polish (docs/briefs/2026-08-29-mylocations-polish-name-colour-selected-home-top.md,
  // item 4): home renders first in the slot column — both at rest and the moment Set-Home is
  // pressed, since this is derived live from userAreas on every render, not a one-time snapshot.
  // Render-only: the DB query's own order (created_at.asc) and userAreas itself are untouched —
  // AreaIdentityFields/AreaColorPicker still receive the raw, unordered userAreas (their "already
  // used colour" checks are order-independent). Only the three slot-button render sites below
  // (mobile top slot, mobile 1-9 grid, desktop 0-9 column) read from this instead.
  const orderedAreas = useMemo(() => {
    const homeIndex = userAreas.findIndex(a => a.is_home);
    if (homeIndex <= 0) return userAreas;
    const home = userAreas[homeIndex];
    return [home, ...userAreas.slice(0, homeIndex), ...userAreas.slice(homeIndex + 1)];
  }, [userAreas]);

  // Render mobile area button
  // 2026-08-26 redesign + create-path build: buttons are pure selectors now — Set Home,
  // Delete, and the colour quick-edit are rehomed into the centre-column editor (item 4 of the
  // brief), not rendered on the button itself, on either viewport.
  const renderMobileAreaButton = (area, index, isActive, isEmpty) => {
    if (isEmpty) {
      return (
        <div
          key={`empty-${index}`}
          onClick={handleStartCreateArea}
          style={{
            width: '100%',
            height: '60px',
            background: theme.bg,
            border: `2px dashed ${theme.primary}`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <span style={{ color: theme.text.secondary, fontSize: '12px', fontWeight: '600' }}>
            {t('locations_button_add_area')}
          </span>
        </div>
      );
    }

    return (
      <div
        key={area?.id ?? `empty-${index}`}
        onClick={() => handleSelectArea(area)}
        style={{
          width: '100%',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: isActive ? `4px solid ${theme.primary}`
            : (isHighlightActive && area?.is_home && area?.is_auto_detected)
              ? '2px solid #3B82F6'
              : '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden',
          // 2026-08-29 UI layout follow-on: fallback is AREA_COLORS[0].hex, not theme.primary
          // (Advisor STRONGLY ADVISE) — item 1 below makes ALL slot text theme.primary
          // unconditionally; a null-colour area (createHomeAreaFromBoundary's own auto-detect paths
          // default color_hex to null on 3 of its 5 call sites — src/utils/homeAreaSelfHealer.js)
          // would otherwise render theme.primary text on a theme.primary background — invisible,
          // and landing on the home slot specifically, the most visible button on the page.
          background: area.color_hex || AREA_COLORS[0].hex,
          // 2026-08-29 UI layout follow-on (item 1): depth via border + shadow, not text colour —
          // a second cue alongside the existing thickened-border convention. Departs from
          // LAYOUT.md:201's Open-entry-frame rule ("the frame is the sole open-state indicator... a
          // redundant hairline that used to double it was removed") — Michael's own explicit,
          // in-browser-reviewed instruction (brief), not a silent variance; noted per the re-flag
          // rule, logged in docs/reuse-backlog.md alongside the 2026-08-26 declined-SUGGEST entry
          // for this same file's border convention.
          boxShadow: isActive ? SELECTED_SLOT_SHADOW : 'none',
          cursor: 'pointer',
          padding: '4px',
          position: 'relative'
        }}
      >
        {/* 2026-08-29 UI layout follow-on (item 1): all slot text is theme.primary (Locality Blue —
            see LAYOUT.md's own reference to "theme.primary (locality blue)") now, selected and
            unselected alike — selection reads from the border + boxShadow above, not text colour.
            textShadow dropped entirely: it was tuned for white text against variable pastel
            backgrounds; kept unconditionally it would read as a dark, muddy blur under dark-blue
            text instead. */}
        <div style={{
          fontFamily: 'monospace',
          fontSize: '10px',
          fontWeight: 'bold',
          color: theme.primary,
          letterSpacing: '0.5px'
        }}>
          {area.area_code}
        </div>
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          color: theme.primary,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          padding: '0 4px'
        }}>
          {/* 2026-08-29 name-default fix: falls back to suggested_name (the freguesia name seeded
              at create) when display_name is still null — shows the default while unsaved,
              without touching what cleanupNamelessArea's orphan sweep tests. */}
          {capitalizeWords(area.display_name ?? area.suggested_name)}
        </div>
        {area.is_home && (
          <div style={{
            position: 'absolute',
            top: '2px',
            right: '4px',
            fontSize: '10px'
          }}>⭐</div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      height: '100vh',
      background: theme.bg,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header - Layered architecture (responsive handled internally) */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext="locality"
        onBack={handleBack}
        headerTitle={t('my_locations_title')}
      />

      {/* MOBILE LAYOUT */}
      {isMobile ? (
        <div style={{
          marginTop: '50px',
          flex: 1,
          overflowY: 'auto',
          padding: '12px',
          paddingBottom: '80px'
        }}>
          {/* Auto-home highlight banner */}
          {isHighlightActive && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '10px 12px',
              background: '#EFF6FF',
              border: '1px solid #93C5FD',
              borderRadius: '8px',
              marginBottom: '10px',
              fontSize: '12px',
              color: '#1E40AF',
              lineHeight: '1.4'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ flex: 1 }}>{tLocation('location.auto_home_banner')}</span>
                <button
                  onClick={dismissHighlight}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1E40AF',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0 2px',
                    lineHeight: '1',
                    flexShrink: 0
                  }}
                  title={tLocation('location.auto_home_dismiss')}
                >
                  &times;
                </button>
              </div>
              <div style={{ fontSize: '11px', color: '#3B82F6' }}>
                {tLocation('location.auto_home_personalise')}
              </div>
              <button
                onClick={() => { dismissHighlight(); if (onBack) onBack(); }}
                style={{
                  alignSelf: 'flex-start',
                  padding: '6px 16px',
                  background: '#024B99',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {tLocation('location.auto_home_confirm')}
              </button>
            </div>
          )}
          {/* Areas Section - 3 columns x 4 rows grid (top row has centered home area) */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridTemplateRows: 'repeat(4, 1fr)',
              gap: '6px'
            }}>
              {/* Top row: Areas label (left), Home/First Area (centered), Empty (right) */}
              <div key="areas-label" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: '600',
                color: theme.text.primary,
                textAlign: 'center',
                padding: '4px'
              }}>
                <div>
                  {t('locations_section_areas')}
                  <div style={{ fontWeight: '400', color: theme.text.secondary, fontSize: '10px' }}>
                    ({userAreas.length}/10)
                  </div>
                </div>
              </div>
              {(() => {
                // 2026-08-29 polish (item 4): orderedAreas, not userAreas — home renders first.
                const area = orderedAreas[0];
                const isActive = selectedArea?.id === area?.id;
                return area
                  ? renderMobileAreaButton(area, 0, isActive, false)
                  : renderMobileAreaButton(null, 0, false, true);
              })()}
              <div key="empty-1" style={{ visibility: 'hidden' }} />

              {/* Remaining 9 areas in 3x3 grid */}
              {[...Array(9)].map((_, i) => {
                const index = i + 1; // Areas 1-9
                const area = orderedAreas[index];
                const isActive = selectedArea?.id === area?.id;
                return area
                  ? renderMobileAreaButton(area, index, isActive, false)
                  : renderMobileAreaButton(null, index, false, true);
              })}
            </div>
          </div>

          {/* Map Section */}
          {/* LocalityMapSelector removed - using boundary-based LocationSelector only */}

          {/* Identity editor card — mobile order per brief item 1: editor, then Save-and-exit,
              then the map (not alongside it, as on desktop). */}
          {(selectedArea || isCreatingArea) && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.text.secondary }}>
                  <span style={{ fontSize: '16px' }}>{getCountryFlag(selectedCountry)}</span>
                  <span>{selectedCountry}</span>
                </div>
                <LanguageSelector countryCode={selectedCountry} />
              </div>
              <h3 style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: theme.text.primary,
                textAlign: 'center'
              }}>
                {tLocation('location.saved_areas.select_areas_title')}
              </h3>
              <p style={{
                margin: '0',
                fontSize: '11px',
                color: theme.text.secondary,
                lineHeight: '1.4',
                textAlign: 'center'
              }}>
                {tLocation('location.saved_areas.select_areas_description', { levels: getSelectableLevelsLabel() })}
              </p>

              <AreaIdentityFields
                displayName={identity.displayName}
                areaCode={identity.areaCode}
                colorHex={identity.colorHex}
                onDisplayNameChange={handleIdentityNameChange}
                onAreaCodeChange={handleIdentityCodeChange}
                onColorHexChange={handleIdentityColorChange}
                nameLabel={t('locations_form_area_name_label')}
                namePlaceholder={t('locations_form_area_name_placeholder')}
                codeLabel={t('locations_form_area_code_label')}
                codePlaceholder={t('locations_form_area_code_placeholder')}
                colorLabel={t('locations_form_area_color_label_required')}
                userAreas={userAreas}
                // 2026-08-29 UI layout follow-on (item 2, Michael's ruling): Set-Home moves to
                // directly under the Area Name field, centred — AreaIdentityFields' own new
                // afterNameField slot (src/components/shared/AreaIdentityFields.jsx), the only
                // caller of the three that passes it.
                // 2026-08-29 is_home database enforcement (Advisor STRONGLY ADVISE, round 2):
                // also gated on selectedArea.display_name — without this, the button stays
                // visible and clickable on every first-time user's still-unsaved first area
                // (is_home is now always false at create; see the create branch's own comment),
                // and clicking it would surface promote_area_to_home's new "unsaved area" RAISE
                // as a raw error modal. The DB-level guard (migration
                // 20260829d_promote_area_to_home_guard.sql) is the actual enforcement; this is
                // the UI half of the brief's "guard the action" instruction.
                // Addendum 2 (2026-09-01 home-picker-redefine-flow-fix-build, Fix 1): the OR
                // clause is the one deliberate exception to that guard's own display_name
                // gate — the specific area a set-home session just seeded (matched by exact id,
                // same STRONGLY-ADVISE-driven idiom as setHomeSessionAreaId's other uses, not a
                // blanket "any unsaved area" relaxation). onClick below still never promotes a
                // nameless row against the DB guard: an unsaved seeded area saves first
                // (handleSaveAndExit's own validation applies unchanged), an already-saved area
                // (any other secondary area, or this one after its own first save) promotes
                // directly, exactly as before.
                afterNameField={selectedArea && !selectedArea.is_home
                  && (selectedArea.display_name || selectedArea.id === setHomeSessionAreaId) && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <StandardButton
                      variant="utility"
                      iconName="Home"
                      label={t('locations_button_set_home')}
                      onClick={() => selectedArea.display_name
                        ? handleSetAreaAsHome(selectedArea.id)
                        : handleSaveAndExit({ navigateAway: false })}
                    />
                  </div>
                )}
              />

              {/* 2026-08-29 UI layout follow-on (items 2/3): Set-Home extracted above (moved under
                  Area Name) — what's left is Edit Colour / Delete, explicitly split left/right per
                  Michael's own words ("leaving Edit Colour on the left and Delete on the right").
                  Applied the same way on desktop for coherence (SUGGEST-tier judgement call, no
                  LAYOUT.md convention mandates either reading — reported, not silently decided). A
                  single child (is_home, no Delete) still left-aligns correctly under
                  space-between — no spacer div needed. */}
              {selectedArea && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', position: 'relative' }}>
                  <StandardButton
                    variant="utility"
                    label={t('locations_button_edit_color')}
                    onClick={() => setShowColorPicker(showColorPicker === selectedArea.id ? null : selectedArea.id)}
                  />
                  {showColorPicker === selectedArea.id && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100 }}>
                      <AreaColorPicker
                        currentColor={selectedArea.color_hex}
                        userAreas={userAreas}
                        onColorSelect={(color) => handleColorChange(selectedArea.id, color)}
                        onClose={() => setShowColorPicker(null)}
                      />
                    </div>
                  )}
                  {!selectedArea.is_home && (
                    <StandardButton
                      variant="destructive"
                      iconName="Trash2"
                      label={t('common:delete')}
                      confirmBefore={false}
                      onClick={() => handleDeleteArea(selectedArea.id, selectedArea.is_home)}
                    />
                  )}
                </div>
              )}

              {/* Own-parish addenda 2 and 3: "Your own parish" — a saved area's base parish,
                  chosen among its parishes. For an area other than home it never changes home. */}
              {offersOwnParish(selectedArea) && ownParishOptions.length > 1 && (
                <div style={{ marginTop: '12px' }}>
                  <ParishSelect
                    label={t('locations_own_parish_label')}
                    hint={t(selectedArea.is_home ? 'locations_own_parish_hint' : 'locations_own_parish_hint_other_area')}
                    options={ownParishOptions}
                    value={selectedArea.primary_boundary_id}
                    onChange={(id) => handleSetOwnParish(selectedArea.id, id)}
                  />
                </div>
              )}

              {/* 2026-08-26 layout-controls-reachable fix: Save-and-exit now renders whenever
                  this card is showing (create mode included), not just once boundary_ids is
                  non-empty — the old gate made it structurally absent for the entire duration of
                  create mode, since selectedArea is null until the first boundary resolves.
                  Advisor-caught: disabled (not omitted) until selectedArea exists — handleSaveAndExit's
                  own `if (!selectedArea) return;` guard would otherwise make an early click on a
                  visibly-enabled-looking button a silent no-op.
                  2026-08-29 UI layout follow-on (item 2): centred, not left-aligned. */}
              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                <StandardButton
                  variant="action"
                  iconName="Save"
                  label={tLocation('save_and_exit')}
                  onClick={() => handleSaveAndExit()}
                  disabled={!selectedArea}
                />
              </div>
            </div>
          )}

          {/* Map card */}
          {(selectedArea || isCreatingArea) && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <UnifiedLocationSelector
                key={`${selectedArea?.id || 'new-area'}-${selectorResetCount}`}
                mode="user"
                allowBoundary={true}
                allowPin={false}
                multiSelect={true}
                initialBoundaries={selectedArea?.boundary_ids || []}
                initialCountry={selectedCountry}
                userLongitude={selectedArea?.is_home ? (COUNTRY_DEFAULT_LNG[selectedCountry] ?? -8.0) : -90}
                onCountryChange={(countryCode) => setSelectedCountry(countryCode)}
                onInitialLoad={handleInitialBoundariesLoad}
                onBoundaryChange={handleAreaBoundaryChange}
                compact={true}
                isHomeAreaBuilding
                copyVariant="newLocation"
                onTargetSeeded={handleTargetSeeded}
                // 2026-09-01 home-picker-redefine-flow-fix-build: both gated on isCreatingArea —
                // an existing, already-selected area (selectedArea truthy) must never re-seed or
                // auto-fill, matching initialBoundaries already winning over seedBoundaryId inside
                // UnifiedLocationSelector's own enrichment effect.
                seedBoundaryId={isCreatingArea ? (activeSeed?.boundaryId ?? null) : null}
                autoFillFirstTierAdjacency={isCreatingArea && !!activeSeed}
                // Addendum 3: re-applies the landing fit once more, specifically on the remount
                // this same set-home session's own create-on-seed causes (selectedArea flips from
                // null to the just-inserted row, changing this component's own key above) — see
                // fitToInitialSelection's own JSDoc in UnifiedLocationSelector.jsx for why the fit
                // doesn't otherwise survive that remount. Gated on the exact area id, same as
                // Save-and-Exit's own unconditional-promotion check — never applies to ordinary
                // area-switching.
                fitToInitialSelection={!!(selectedArea && setHomeSessionAreaId && selectedArea.id === setHomeSessionAreaId)}
              />
            </div>
          )}

          {/* Localities list removed - using boundary_ids only */}
        </div>
      ) : (
        /* DESKTOP LAYOUT - Areas left, Map+Localities centre */
        <div style={{
          marginTop: '80px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          // 2026-08-26 layout-controls-reachable fix: LAYOUT.md's "Content bottom margin: 120px —
          // clears footer" was never applied here (only the top/header clearance was) — the fixed
          // FooterContainer (viewType="list", 120px, z-index:1100) rendered below could paint over
          // page-bottom content with nothing reserving space for it. Defense-in-depth alongside
          // removing the centre column's window-bound flex:1 stretch (below).
          paddingBottom: '120px',
          overflow: 'hidden'
        }}>
        {/* Auto-home highlight banner (desktop) */}
        {isHighlightActive && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            background: '#EFF6FF',
            border: '1px solid #93C5FD',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#1E40AF',
            lineHeight: '1.5'
          }}>
            <div style={{ flex: 1 }}>
              <div>{tLocation('location.auto_home_banner')}</div>
              <div style={{ fontSize: '12px', color: '#3B82F6', marginTop: '4px' }}>
                {tLocation('location.auto_home_personalise')}
              </div>
            </div>
            <button
              onClick={() => { dismissHighlight(); if (onBack) onBack(); }}
              style={{
                padding: '6px 20px',
                background: '#024B99',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              {tLocation('location.auto_home_confirm')}
            </button>
            <button
              onClick={dismissHighlight}
              style={{
                background: 'none',
                border: 'none',
                color: '#1E40AF',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0 4px',
                lineHeight: '1',
                flexShrink: 0
              }}
              title={tLocation('location.auto_home_dismiss')}
            >
              &times;
            </button>
          </div>
        )}
        <div style={{
          flex: 1,
          display: 'flex',
          gap: '20px',
          overflow: 'hidden'
        }}>
        {/* Left Column - Areas Management (10 Fixed Slots) — pure selectors, no embedded
            controls (Set Home / Delete / colour quick-edit are rehomed into the centre
            column's editor for the selected area, per this build's item 4). */}
        <div style={{
          width: '160px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: '10px',
          // 2026-09-03 short-viewport scroll-fix build: mirrors the centre column's
          // 2026-08-26 overflow fix below — without this, the 10 fixed 75px slots
          // (~732px) can't shrink below their own content height (flex min-height:auto
          // on an overflow:visible item), inflating the row and clipping the last
          // "+ Create Area" slots on short viewports. minHeight:0 lets the column
          // shrink; overflowY:auto scrolls its own content instead of clipping it.
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin'
        }}>
          {/* 2026-08-29 polish (item 4): orderedAreas, not userAreas — home renders first. */}
          {[...Array(10)].map((_, index) => {
            const area = orderedAreas[index];
            const isActive = selectedArea?.id === area?.id;
            const isEmpty = !area;

            return (
              <div
                key={area?.id ?? `empty-${index}`}
                onClick={() => {
                  if (isEmpty) {
                    handleStartCreateArea();
                  } else {
                    handleSelectArea(area);
                  }
                }}
                style={{
                  height: '75px',
                  // 2026-08-29 UI layout follow-on: fallback is AREA_COLORS[0].hex, not
                  // theme.primary (Advisor STRONGLY ADVISE) — all slot text below is now
                  // theme.primary unconditionally; a null-colour area (createHomeAreaFromBoundary's
                  // own auto-detect paths default color_hex to null on 3 of its 5 call sites —
                  // src/utils/homeAreaSelfHealer.js) would otherwise render theme.primary text on a
                  // theme.primary background — invisible, landing on the home slot specifically.
                  background: (isHighlightActive && area?.is_home && area?.is_auto_detected) ? '#EFF6FF'
                    : (isEmpty ? theme.bg : (area.color_hex || AREA_COLORS[0].hex)),
                  border: isActive ? `4px solid ${theme.primary}`
                    : (isHighlightActive && area?.is_home && area?.is_auto_detected) ? '3px solid #3B82F6'
                    : `2px solid ${theme.primary}`,
                  borderRadius: '12px',
                  padding: '3px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'background 0.2s',
                  position: 'relative',
                  marginBottom: index === 9 ? '0' : '-2px',
                  // 2026-08-29 UI layout follow-on (item 1): depth via border + shadow, not text
                  // colour — departs from LAYOUT.md:201's Open-entry-frame rule ("the frame is the
                  // sole open-state indicator"), Michael's own explicit in-browser instruction, not
                  // a silent variance — logged in docs/reuse-backlog.md. zIndex lifts the active
                  // button above its neighbour so the -2px stacking overlap (marginBottom above)
                  // doesn't trim the shadow's bottom edge for 9 of the 10 stacked slots.
                  boxShadow: isActive ? SELECTED_SLOT_SHADOW : 'none',
                  zIndex: isActive ? 1 : 'auto'
                }}
              >
                {isEmpty ? (
                  <div style={{
                    textAlign: 'center',
                    color: theme.text.secondary,
                    fontSize: '12px',
                    fontFamily: 'OpenSauceSans',
                    fontWeight: '600',
                    width: '100%'
                  }}>
                    {t('locations_button_create_area')}
                  </div>
                ) : (
                  <>
                    {/* 2026-08-29 UI layout follow-on (item 1): all slot text is theme.primary
                        (Locality Blue) now, selected and unselected alike — selection reads from the
                        border + boxShadow above, not text colour. textShadow dropped entirely: it
                        was tuned for white text against variable pastel backgrounds; kept
                        unconditionally it would read as a dark, muddy blur under dark-blue text. */}
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: theme.primary,
                      letterSpacing: '1px'
                    }}>
                      {area.area_code}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      fontFamily: 'OpenSauceSans',
                      color: theme.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      width: '100%',
                      padding: '0 4px'
                    }}>
                      {/* 2026-08-29 name-default fix: falls back to suggested_name (see mobile's
                          identical comment above). */}
                      {capitalizeWords(area.display_name ?? area.suggested_name)}
                    </div>
                    {area.is_home && (
                      <div style={{ position: 'absolute', top: '4px', right: '6px', fontSize: '13px' }}>⭐</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Centre Column - country context, heading/prompt, identity editor + rehomed
            Set Home / Delete / colour-quick-edit, Save-and-exit */}
        <div style={{
          width: '280px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          // 2026-08-26 layout-controls-reachable fix (Advisor-caught): with the card below no
          // longer flex:1-stretched to exactly fill this wrapper, a plain `overflow: hidden` here
          // would silently clip Save-and-exit again on any desktop viewport short enough that the
          // card's natural content height exceeds the row's available height — the same failure
          // this brief exists to close, just probabilistic instead of guaranteed. Scroll instead
          // of clip, so the control is always reachable even if that means scrolling to it.
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {(selectedArea || isCreatingArea) && (
            <div style={{
              background: theme.cardBg,
              border: '5px solid white',
              borderRadius: '20px',
              padding: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '8px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: theme.text.secondary }}>
                  <span style={{ fontSize: '18px' }}>{getCountryFlag(selectedCountry)}</span>
                  <span>{selectedCountry}</span>
                </div>
                <LanguageSelector countryCode={selectedCountry} />
              </div>

              <h2 style={{
                margin: '0 0 6px 0',
                fontSize: '18px',
                fontFamily: 'OpenSauceSans',
                fontWeight: '600',
                color: theme.text.primary,
                textAlign: 'center'
              }}>
                {tLocation('location.saved_areas.select_areas_title')}
              </h2>
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '13px',
                color: theme.text.secondary,
                lineHeight: '1.5',
                textAlign: 'center'
              }}>
                {tLocation('location.saved_areas.select_areas_description', { levels: getSelectableLevelsLabel() })}
              </p>

              <AreaIdentityFields
                displayName={identity.displayName}
                areaCode={identity.areaCode}
                colorHex={identity.colorHex}
                onDisplayNameChange={handleIdentityNameChange}
                onAreaCodeChange={handleIdentityCodeChange}
                onColorHexChange={handleIdentityColorChange}
                nameLabel={t('locations_form_area_name_label')}
                namePlaceholder={t('locations_form_area_name_placeholder')}
                codeLabel={t('locations_form_area_code_label')}
                codePlaceholder={t('locations_form_area_code_placeholder')}
                colorLabel={t('locations_form_area_color_label_required')}
                userAreas={userAreas}
                // 2026-08-29 UI layout follow-on (item 2, Michael's ruling): Set-Home moves to
                // directly under the Area Name field, centred — same as mobile above, via
                // AreaIdentityFields' new afterNameField slot.
                // 2026-08-29 is_home database enforcement (Advisor STRONGLY ADVISE, round 2):
                // also gated on selectedArea.display_name — without this, the button stays
                // visible and clickable on every first-time user's still-unsaved first area
                // (is_home is now always false at create; see the create branch's own comment),
                // and clicking it would surface promote_area_to_home's new "unsaved area" RAISE
                // as a raw error modal. The DB-level guard (migration
                // 20260829d_promote_area_to_home_guard.sql) is the actual enforcement; this is
                // the UI half of the brief's "guard the action" instruction.
                // Addendum 2 (2026-09-01 home-picker-redefine-flow-fix-build, Fix 1): the OR
                // clause is the one deliberate exception to that guard's own display_name
                // gate — the specific area a set-home session just seeded (matched by exact id,
                // same STRONGLY-ADVISE-driven idiom as setHomeSessionAreaId's other uses, not a
                // blanket "any unsaved area" relaxation). onClick below still never promotes a
                // nameless row against the DB guard: an unsaved seeded area saves first
                // (handleSaveAndExit's own validation applies unchanged), an already-saved area
                // (any other secondary area, or this one after its own first save) promotes
                // directly, exactly as before.
                afterNameField={selectedArea && !selectedArea.is_home
                  && (selectedArea.display_name || selectedArea.id === setHomeSessionAreaId) && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <StandardButton
                      variant="utility"
                      iconName="Home"
                      label={t('locations_button_set_home')}
                      onClick={() => selectedArea.display_name
                        ? handleSetAreaAsHome(selectedArea.id)
                        : handleSaveAndExit({ navigateAway: false })}
                    />
                  </div>
                )}
              />

              {/* 2026-08-29 UI layout follow-on (items 2/3): same restructure as mobile above —
                  Set-Home extracted (moved under Area Name); Edit Colour / Delete split left/right. */}
              {selectedArea && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', position: 'relative' }}>
                  <StandardButton
                    variant="utility"
                    label={t('locations_button_edit_color')}
                    onClick={() => setShowColorPicker(showColorPicker === selectedArea.id ? null : selectedArea.id)}
                  />
                  {showColorPicker === selectedArea.id && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100 }}>
                      <AreaColorPicker
                        currentColor={selectedArea.color_hex}
                        userAreas={userAreas}
                        onColorSelect={(color) => handleColorChange(selectedArea.id, color)}
                        onClose={() => setShowColorPicker(null)}
                      />
                    </div>
                  )}
                  {!selectedArea.is_home && (
                    <StandardButton
                      variant="destructive"
                      iconName="Trash2"
                      label={t('common:delete')}
                      confirmBefore={false}
                      onClick={() => handleDeleteArea(selectedArea.id, selectedArea.is_home)}
                    />
                  )}
                </div>
              )}

              {/* Own-parish addenda 2 and 3: "Your own parish" — a saved area's base parish,
                  chosen among its parishes. For an area other than home it never changes home. */}
              {offersOwnParish(selectedArea) && ownParishOptions.length > 1 && (
                <div style={{ marginTop: '12px' }}>
                  <ParishSelect
                    label={t('locations_own_parish_label')}
                    hint={t(selectedArea.is_home ? 'locations_own_parish_hint' : 'locations_own_parish_hint_other_area')}
                    options={ownParishOptions}
                    value={selectedArea.primary_boundary_id}
                    onChange={(id) => handleSetOwnParish(selectedArea.id, id)}
                  />
                </div>
              )}

              {/* 2026-08-26 layout-controls-reachable fix: this card no longer force-stretches
                  via flex:1 to fill the row's full (window-bound) height with a spacer pushing
                  Save-and-exit to that stretched bottom edge — that is what drove the button into
                  the fixed footer's 120px/z-index:1100 strip. The card now sizes to its own
                  content, so Save-and-exit sits right after the rehomed controls, comfortably
                  clear of the footer. It also renders unconditionally here (was gated on
                  boundary_ids.length > 0, which is structurally never true in create mode before
                  the first boundary resolves — see the mobile fix's comment above). Advisor-caught:
                  disabled (not omitted) until selectedArea exists, so an early click in create
                  mode is visibly inert rather than a silent no-op against handleSaveAndExit's own
                  `if (!selectedArea) return;` guard.
                  2026-08-29 UI layout follow-on (item 2): centred, not left-aligned. */}
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
                <StandardButton
                  variant="action"
                  iconName="Save"
                  label={tLocation('save_and_exit')}
                  onClick={() => handleSaveAndExit()}
                  disabled={!selectedArea}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Column - the map (rich crosshair) */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          // 2026-09-03 short-viewport scroll-fix build: was overflow:'hidden', which
          // clipped the Country control, the "Set your new location" button (renders
          // above the map) and/or the fixed-500px map itself on short viewports with
          // no way to reach the clipped part. Mirrors the centre column's own
          // 2026-08-26 overflowY:auto fix (:1636-1649) so all three columns scroll
          // independently instead of clipping. minHeight:0 lets this flex:1 child
          // shrink below its content's natural height.
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          maxWidth: '600px'
        }}>
          {(selectedArea || isCreatingArea) && (
            <div style={{
              background: theme.cardBg,
              border: '5px solid white',
              borderRadius: '20px',
              padding: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '8px'
            }}>
              <UnifiedLocationSelector
                key={`${selectedArea?.id || 'new-area'}-${selectorResetCount}`}
                mode="user"
                allowBoundary={true}
                allowPin={false}
                multiSelect={true}
                initialBoundaries={selectedArea?.boundary_ids || []}
                initialCountry={selectedCountry}
                userLongitude={selectedArea?.is_home ? (COUNTRY_DEFAULT_LNG[selectedCountry] ?? -8.0) : -90}
                onCountryChange={(countryCode) => setSelectedCountry(countryCode)}
                onInitialLoad={handleInitialBoundariesLoad}
                onBoundaryChange={handleAreaBoundaryChange}
                compact={true}
                isHomeAreaBuilding
                copyVariant="newLocation"
                onTargetSeeded={handleTargetSeeded}
                // 2026-09-01 home-picker-redefine-flow-fix-build: both gated on isCreatingArea —
                // an existing, already-selected area (selectedArea truthy) must never re-seed or
                // auto-fill, matching initialBoundaries already winning over seedBoundaryId inside
                // UnifiedLocationSelector's own enrichment effect.
                seedBoundaryId={isCreatingArea ? (activeSeed?.boundaryId ?? null) : null}
                autoFillFirstTierAdjacency={isCreatingArea && !!activeSeed}
                // Addendum 3: re-applies the landing fit once more, specifically on the remount
                // this same set-home session's own create-on-seed causes (selectedArea flips from
                // null to the just-inserted row, changing this component's own key above) — see
                // fitToInitialSelection's own JSDoc in UnifiedLocationSelector.jsx for why the fit
                // doesn't otherwise survive that remount. Gated on the exact area id, same as
                // Save-and-Exit's own unconditional-promotion check — never applies to ordinary
                // area-switching.
                fitToInitialSelection={!!(selectedArea && setHomeSessionAreaId && selectedArea.id === setHomeSessionAreaId)}
              />
            </div>
          )}
        </div>
      </div>
      </div>
      )}

      {/* Footer - Desktop only */}
      {!isMobile && (
        <FooterContainer
          viewType="list"
          currentView={t('my_locations_title')}
          currentContext={{
            // 2026-08-29 name-default fix (Advisor-caught): falls back to suggested_name, same as
            // the editor field and slot button — without this, an unsaved area would show its
            // freguesia name everywhere else on screen but "No area selected" here, for the same
            // selected row, at the same time.
            name: (selectedArea?.display_name ?? selectedArea?.suggested_name) || t('locations_footer_no_area_selected'),
            type: 'user-locations',
            component: 'MyLocations',
            selectedArea: selectedArea ? `${selectedArea.display_name ?? selectedArea.suggested_name} (${selectedArea.area_code})` : null,
            selectedLocality: null, // userLocalities removed - using boundary_ids
            itemId: selectedArea?.id || null,
            itemType: 'area'
          }}
        >
          <FooterButtonsList />
        </FooterContainer>
      )}

      <ConfirmationModal
        visible={modal.visible}
        // 2026-08-28 UX companion fix (Fault 3): was hardcoded to "Confirmation" for every
        // modal state, including outright errors — matches the established pattern already
        // used elsewhere (e.g. AddEditGroup.jsx), where an error call site sets modal.title
        // explicitly and everything else keeps the default.
        title={modal.title || t('common:confirmation')}
        message={modal.message}
        cancelLabel={modal.cancelLabel}
        onOk={modal.onOk}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default MyLocations;
